/**
 * The shell, and the role gate.
 *
 * `GET /api/me` runs once on load and decides which of three applications the
 * visitor is in: the ops console, the seller dashboard, or the login screen.
 * The gate is a convenience, not a control — the server scopes every seller
 * query to the signed-in seller regardless of what the client renders, and it
 * must stay that way. Nothing here should ever become the reason a seller
 * cannot read another seller's data.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router';
import { getMe, logout, type AuthenticatedUser } from './api.js';
import { useAsync } from './useAsync.js';
import { Callout, ErrorCallout, Loading } from './ui.js';
import { LoginPage } from './pages/LoginPage.js';
import { QueuePage } from './pages/QueuePage.js';
import { ClaimsPage } from './pages/ClaimsPage.js';
import { ClaimDetailPage } from './pages/ClaimDetailPage.js';
import { SellerPage } from './pages/SellerPage.js';

export function App(): ReactNode {
  const { data, error, loading, reload } = useAsync(() => getMe(), []);

  if (loading && !data) {
    return (
      <main>
        <Loading label="Checking your session" rows={2} />
      </main>
    );
  }

  // A failed /api/me is a dead API, not a signed-out user. Showing the login
  // form here would send the operator round a loop that cannot terminate.
  if (error) {
    return (
      <main>
        <ErrorCallout error={error} />
      </main>
    );
  }

  const user = data?.user ?? null;
  if (!user) return <LoginPage onSignedIn={reload} />;

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        Skip to main content
      </a>
      <Masthead user={user} onSignedOut={reload} />
      <main id="main" tabIndex={-1}>
        {user.role === 'ops' ? <OpsRoutes /> : <SellerRoutes />}
      </main>
    </div>
  );
}

function OpsRoutes(): ReactNode {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/ops/queue" replace />} />
      <Route path="/ops" element={<Navigate to="/ops/queue" replace />} />
      <Route path="/ops/queue" element={<QueuePage />} />
      <Route path="/ops/claims" element={<ClaimsPage />} />
      <Route path="/ops/claims/:claimId" element={<ClaimDetailPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function SellerRoutes(): ReactNode {
  return (
    <Routes>
      <Route path="/" element={<SellerPage />} />
      <Route path="/seller" element={<SellerPage />} />
      {/* An ops URL pasted to a seller is a wrong turn, not an error page. */}
      <Route path="/ops/*" element={<Navigate to="/" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function NotFound(): ReactNode {
  return (
    <Callout tone="info" title="No such page">
      <p>
        The address does not match anything in this application. Use the navigation above to get
        back.
      </p>
    </Callout>
  );
}

function Masthead({
  user,
  onSignedOut,
}: {
  user: AuthenticatedUser;
  onSignedOut: () => void;
}): ReactNode {
  const [signingOut, setSigningOut] = useState(false);

  async function signOut(): Promise<void> {
    setSigningOut(true);
    try {
      await logout();
    } finally {
      // Reload regardless: if the call failed, the session may still be gone,
      // and re-reading /api/me is the only way to find out which.
      setSigningOut(false);
      onSignedOut();
    }
  }

  return (
    <header className="masthead">
      <div className="brand">
        FBT Recovery
        <small>{user.role === 'ops' ? 'Ops console' : 'Seller dashboard'}</small>
      </div>

      {user.role === 'ops' ? (
        <nav aria-label="Sections">
          <NavLink className="nav-link" to="/ops/queue">
            Findings queue
          </NavLink>
          <NavLink className="nav-link" to="/ops/claims">
            Open claims
          </NavLink>
        </nav>
      ) : null}

      <div className="masthead-end">
        <ThemeToggle />
        <div className="whoami">
          <strong>{user.email}</strong>
          {user.role === 'ops' ? 'All shops' : 'Your shops only'}
        </div>
        <button className="btn btn-quiet" onClick={() => void signOut()} disabled={signingOut}>
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </header>
  );
}

type ThemeChoice = 'system' | 'light' | 'dark';
const THEME_KEY = 'fbt.theme';

/**
 * Light, dark, or whatever the operating system says.
 *
 * The stylesheet already follows `prefers-color-scheme`, so this exists for the
 * case the media query cannot cover: an operator on a light desktop working a
 * queue at night, or the reverse. The choice is stamped on `<html>` and wins
 * over the system preference in both directions.
 */
function ThemeToggle(): ReactNode {
  const [choice, setChoice] = useState<ThemeChoice>(() => readStoredTheme());

  useEffect(() => {
    const root = document.documentElement;
    if (choice === 'system') {
      delete root.dataset.theme;
      localStorage.removeItem(THEME_KEY);
    } else {
      root.dataset.theme = choice;
      localStorage.setItem(THEME_KEY, choice);
    }
  }, [choice]);

  const cycle = useCallback(() => {
    setChoice((current) =>
      current === 'system' ? 'light' : current === 'light' ? 'dark' : 'system',
    );
  }, []);

  const label = choice === 'system' ? 'Theme: system' : `Theme: ${choice}`;
  return (
    <button className="btn btn-quiet btn-sm" onClick={cycle} aria-label={`${label}. Change theme.`}>
      {label}
    </button>
  );
}

function readStoredTheme(): ThemeChoice {
  const stored = localStorage.getItem(THEME_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}
